// SampleAdoptCard — "Add to catalog" panel for RECEIVED samples (admin shell
// only; the factory never sees it — S7). Variation-first mirror of the
// unmapped-SKU adopt panel (PR #375): default mode adds the sample as a new
// variation of a suggested existing product; create-a-new-product stays one
// toggle away. Optionally onboards the physical sample as opening stock
// ("Add to inventory") through the same canonical add-variant service.

import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BookPlus, Check, ExternalLink, Loader2, PackagePlus } from 'lucide-react'
import { Card } from '../../components/ui/card'
import { apiUrl } from '../../lib/api'
import { toast } from '../../lib/toast'
import { type Sample } from './lib'
import { adoptSample, getAdoptCandidates, type AdoptCandidate } from './api'
import { useT } from '../../i18n'
import { sampleAdoptCardI18n } from '../../i18n/samples/adopt-card'
import { samplesCommonI18n } from '../../i18n/samples/common'

type EcosystemOption = { id: string; name: string }
type WarehouseOption = { id: string; name: string; code: string }

async function fetchEcosystems(): Promise<EcosystemOption[]> {
  const res = await fetch(apiUrl('/fulfillment-ecosystems'))
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const j = (await res.json()) as { items?: Array<Record<string, unknown>> }
  return (j.items ?? [])
    .filter((r) => r.active !== false)
    .map((r) => ({ id: String(r.id), name: String(r.display_name ?? r.key ?? r.id) }))
}

async function fetchWarehouses(): Promise<WarehouseOption[]> {
  const res = await fetch(apiUrl('/warehouses?active_only=true'))
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const j = (await res.json()) as { items?: Array<Record<string, unknown>> }
  return (j.items ?? []).map((r) => ({
    id: String(r.id),
    name: String(r.name ?? r.code ?? r.id),
    code: String(r.code ?? ''),
  }))
}

function suggestSkuCode(title: string | null, axisValue: string): string {
  const slug = (s: string) =>
    s
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 24)
  const base = slug(title || 'SAMPLE')
  const axis = slug(axisValue)
  return axis ? `${base}-${axis}` : base
}

const inputCls =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50'
const labelCls = 'block text-xs font-medium text-slate-700'

export function SampleAdoptCard({ sample, onChanged }: { sample: Sample; onChanged: () => void }) {
  const qc = useQueryClient()
  const t = useT(sampleAdoptCardI18n)
  const sc = useT(samplesCommonI18n)
  const adopted = !!sample.adopted_master_catalog_id

  const candidatesQ = useQuery({
    queryKey: ['sample-adopt-candidates', sample.id],
    queryFn: () => getAdoptCandidates(sample.id),
    enabled: !adopted,
  })
  const ecosystemsQ = useQuery({
    queryKey: ['fulfillment-ecosystems-options'],
    queryFn: fetchEcosystems,
    enabled: !adopted,
  })
  const warehousesQ = useQuery({
    queryKey: ['warehouse-options'],
    queryFn: fetchWarehouses,
    enabled: !adopted,
  })

  const candidates = useMemo(() => candidatesQ.data?.candidates ?? [], [candidatesQ.data])

  const [mode, setMode] = useState<'add_variation' | 'create_product'>('add_variation')
  const [candidateId, setCandidateId] = useState<string | null>(null)
  const [axes, setAxes] = useState<Record<string, string>>({})
  const [title, setTitle] = useState(sample.title || '')
  const [category, setCategory] = useState('other')
  const [ecosystemId, setEcosystemId] = useState('')
  const [axisName, setAxisName] = useState('Color')
  const [axisValue, setAxisValue] = useState('')
  const [skuCode, setSkuCode] = useState('')
  const [skuTouched, setSkuTouched] = useState(false)
  const [addInventory, setAddInventory] = useState(false)
  const [warehouseId, setWarehouseId] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [busy, setBusy] = useState(false)

  const selected: AdoptCandidate | null = useMemo(
    () => candidates.find((c) => c.master_catalog_id === candidateId) ?? null,
    [candidates, candidateId],
  )
  // Default-select the top candidate once suggestions arrive; fall back to
  // create_product when nothing matched.
  const [seeded, setSeeded] = useState(false)
  if (!seeded && candidatesQ.data) {
    setSeeded(true)
    if (candidates.length > 0) setCandidateId(candidates[0].master_catalog_id)
    else setMode('create_product')
  }

  const axisNames = selected ? selected.options.map((o) => o.name) : []
  const firstAxisValue = mode === 'add_variation' ? Object.values(axes).find(Boolean) || '' : axisValue
  const effectiveSku = skuTouched
    ? skuCode
    : suggestSkuCode(mode === 'add_variation' ? (selected?.title ?? sample.title) : title, firstAxisValue)

  const canSubmit =
    !busy &&
    effectiveSku.trim().length > 0 &&
    (!addInventory || (warehouseId && quantity >= 1)) &&
    (mode === 'add_variation'
      ? !!selected && axisNames.length > 0 && axisNames.every((n) => (axes[n] || '').trim())
      : title.trim().length > 0 && !!ecosystemId && axisName.trim().length > 0 && axisValue.trim().length > 0)

  async function submit() {
    setBusy(true)
    try {
      const result = await adoptSample(sample.id, {
        mode,
        sku_code: effectiveSku.trim(),
        ...(mode === 'add_variation'
          ? { master_catalog_id: selected!.master_catalog_id, axes }
          : {
              title: title.trim(),
              category: category.trim() || 'other',
              fulfillment_ecosystem_id: ecosystemId,
              axis_name: axisName.trim(),
              axis_value: axisValue.trim(),
            }),
        ...(addInventory ? { add_inventory: true, warehouse_id: warehouseId, quantity } : {}),
      })
      toast.success(t.addedToCatalog, {
        description: `${result.adopted_sku_code} → ${result.adopted_product_title}${result.inventory_added ? t.inventorySuffix : ''}`,
      })
      void qc.invalidateQueries({ queryKey: ['sample-adopt-candidates', sample.id] })
      onChanged()
    } catch (e) {
      toast.error(t.addFailed, { description: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }

  if (adopted) {
    return (
      <Card className="flex items-center justify-between gap-3 border-emerald-200 bg-emerald-50/50 p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
          <Check size={16} /> {t.inCatalog}
          {sample.adopted_at ? (
            <span className="font-normal text-emerald-700/80">{t.since(sc.fmtDate(sample.adopted_at))}</span>
          ) : null}
        </div>
        <Link
          to="/admin/catalog/products/$id"
          params={{ id: sample.adopted_master_catalog_id! }}
          className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800 hover:underline"
        >
          {t.openProduct} <ExternalLink size={12} />
        </Link>
      </Card>
    )
  }

  return (
    <Card className="space-y-4 border-teal-200 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
          <BookPlus size={18} className="text-teal-700" /> {t.addToCatalog}
        </h2>
        <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs">
          <button
            className={`rounded-md px-2.5 py-1 ${mode === 'add_variation' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setMode('add_variation')}
          >
            {t.variationOfExisting}
          </button>
          <button
            className={`rounded-md px-2.5 py-1 ${mode === 'create_product' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setMode('create_product')}
          >
            {t.newProduct}
          </button>
        </div>
      </div>

      {mode === 'add_variation' && (
        <div className="space-y-3">
          {candidatesQ.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> {t.lookingForSimilar}</div>
          ) : candidates.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">
              {t.noSimilar}
            </div>
          ) : (
            <div className="space-y-2">
              {candidates.map((c) => (
                <button
                  key={c.master_catalog_id}
                  onClick={() => { setCandidateId(c.master_catalog_id); setAxes({}) }}
                  className={`w-full rounded-lg border p-3 text-left text-sm transition ${c.master_catalog_id === candidateId ? 'border-teal-400 bg-teal-50/60' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate font-medium text-slate-800">{c.title}</span>
                    <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-slate-600">{c.display_code || c.platform_sku}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {t.skuCount(c.sku_count)}{t.match(Math.round(c.score * 100))}
                    {!c.variant_addable && <span className="ml-1 text-amber-600">{t.multiVariantHint}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {selected && selected.variant_addable && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {selected.options.map((o) => (
                <div key={o.name} className="space-y-1">
                  <label className={labelCls}>{o.name}</label>
                  <input
                    list={`adopt-axis-${o.name}`}
                    value={axes[o.name] || ''}
                    onChange={(e) => setAxes({ ...axes, [o.name]: e.target.value })}
                    placeholder={t.newAxisValue(o.name)}
                    className={inputCls}
                  />
                  <datalist id={`adopt-axis-${o.name}`}>
                    {o.values.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
              ))}
            </div>
          )}
          {selected && !selected.variant_addable && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              {t.multipleParents}
            </div>
          )}
        </div>
      )}

      {mode === 'create_product' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <label className={labelCls}>{t.productTitle}</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
          </div>
          <div className="space-y-1">
            <label className={labelCls}>{t.category}</label>
            <input value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} />
          </div>
          <div className="space-y-1">
            <label className={labelCls}>{t.ecosystem}</label>
            <select value={ecosystemId} onChange={(e) => setEcosystemId(e.target.value)} className={inputCls}>
              <option value="">{t.select}</option>
              {(ecosystemsQ.data ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className={labelCls}>{t.firstAxis}</label>
            <input value={axisName} onChange={(e) => setAxisName(e.target.value)} placeholder={t.axisPlaceholder} className={inputCls} />
          </div>
          <div className="space-y-1">
            <label className={labelCls}>{t.value}</label>
            <input value={axisValue} onChange={(e) => setAxisValue(e.target.value)} placeholder={t.valuePlaceholder} className={inputCls} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 border-t border-slate-100 pt-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className={labelCls}>{t.skuCode}</label>
          <input
            value={effectiveSku}
            onChange={(e) => { setSkuTouched(true); setSkuCode(e.target.value) }}
            className={`${inputCls} font-mono`}
          />
        </div>
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700">
            <input type="checkbox" className="h-3.5 w-3.5" checked={addInventory} onChange={(e) => setAddInventory(e.target.checked)} />
            <PackagePlus size={14} className="text-teal-700" /> {t.addToInventory}
          </label>
          {addInventory && (
            <div className="flex gap-2">
              <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className={inputCls}>
                <option value="">{t.warehouse}</option>
                {(warehousesQ.data ?? []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
              <input
                type="number" min={1} max={1000} value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                className={`${inputCls} w-20`}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          disabled={!canSubmit}
          onClick={() => void submit()}
          className="inline-flex items-center gap-1.5 rounded bg-teal-600 px-4 py-2 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <BookPlus size={14} />} {t.addToCatalog}
        </button>
      </div>
    </Card>
  )
}
